import { useCallback, useEffect, useEffectEvent, useMemo, useRef } from 'react';
import type {
  TrackedUiEventFields,
  TrackedUiEventName,
  UiUsageEventFields,
  UiUsageOutcomeEventName,
} from '@packages/types';
import { track } from './ui-usage.tracker';

/** Default dedupe key: the fields themselves, in a key order that does not depend on how they were written. */
function fieldsKey(fields: object): string {
  return JSON.stringify(Object.entries(fields).sort(([a], [b]) => a.localeCompare(b)));
}

function resolveKey(fields: object | null, key: string | undefined): string | null {
  if (fields === null) return null;
  return key ?? fieldsKey(fields);
}

/**
 * Records `name` once per distinct key for the life of the mount. `null` fields mean the data
 * is not ready yet: nothing is recorded until they resolve. Dedupes through a ref, which
 * survives StrictMode's simulated remount.
 */
export function useTrackOnce<N extends TrackedUiEventName>(
  name: N,
  fields: TrackedUiEventFields<N> | null,
  key?: string,
): void {
  const seenRef = useRef(new Set<string>());
  const resolvedKey = resolveKey(fields, key);
  const record = useEffectEvent((resolved: string) => {
    if (fields === null || seenRef.current.has(resolved)) return;
    seenRef.current.add(resolved);
    track(name, fields);
  });

  useEffect(() => {
    if (resolvedKey !== null) record(resolvedKey);
  }, [resolvedKey]);
}

/**
 * Records `name` on the first resolved key and again on every change of key, so A, B, A is three
 * events and a repeated value is none. `null` fields mean "not ready"; they leave the last key alone.
 */
export function useTrackOnChange<N extends TrackedUiEventName>(
  name: N,
  fields: TrackedUiEventFields<N> | null,
  key?: string,
): void {
  const lastKeyRef = useRef<string | null>(null);
  const resolvedKey = resolveKey(fields, key);
  const record = useEffectEvent((resolved: string) => {
    if (fields === null || lastKeyRef.current === resolved) return;
    lastKeyRef.current = resolved;
    track(name, fields);
  });

  useEffect(() => {
    if (resolvedKey !== null) record(resolvedKey);
  }, [resolvedKey]);
}

/**
 * One open-to-close attempt. `start` marks the attempt open (idempotent while open); the first
 * `settle` after it records the event and closes the attempt. A `settle` with no open attempt,
 * and an attempt left open at unmount, record nothing.
 */
export function useTrackOutcome<N extends UiUsageOutcomeEventName>(
  name: N,
): { start(): void; settle(fields: UiUsageEventFields<N>): void } {
  const openRef = useRef(false);
  const start = useCallback(() => {
    openRef.current = true;
  }, []);
  const settle = useCallback(
    (fields: UiUsageEventFields<N>) => {
      if (!openRef.current) return;
      openRef.current = false;
      track(name, fields);
    },
    [name],
  );
  return useMemo(() => ({ start, settle }), [start, settle]);
}
